import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { HiCheckCircle, HiExclamationCircle } from "react-icons/hi"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"

import { changePassword, updateProfile } from "../../../actions/auth"
import Breadcrumb from "../../../components/breadcrumb"
import type { AppDispatch, RootState } from "../../../store"
import { ChangePasswordForm } from "./components/ChangePasswordForm"
import { EditProfileForm, type EditProfileFormValues } from "./components/EditProfileForm"

const breadcrumbs = [
  { name: "account.personal.breadcrumb.home", url: "/handbooks" },
  { name: "account.personal.breadcrumb.settings" }
]

const SUPPORTED_LANGUAGES = [
  { value: "en", label: "English" },
  { value: "fi", label: "Suomi" }
]

const PersonalSettingsContainer: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const user = useSelector((state: RootState) => state.auth.user)
  const [isEditing, setIsEditing] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  const initialValues: EditProfileFormValues = {
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    email: user?.email ?? "",
    language: user?.language ?? "en"
  }

  const handleSubmit = async (values: EditProfileFormValues) => {
    try {
      const response = await dispatch(updateProfile(values))
      if (response.emailChanged) {
        toast.success(t("account.personal.notifications.updateSuccessEmailChanged"))
      } else {
        toast.success(t("account.personal.notifications.updateSuccess"))
      }
      return response
    } catch (error) {
      toast.error(t("account.personal.notifications.updateFailed"))
      throw error
    }
  }

  const handleChangePassword = async (values: { currentPassword: string; newPassword: string }) => {
    try {
      await dispatch(changePassword(values))
      toast.success(t("account.personal.notifications.passwordChanged"))
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 401) {
        toast.error(t("account.personal.notifications.currentPasswordIncorrect"))
      } else {
        toast.error(t("account.personal.notifications.passwordChangeFailed"))
      }
      throw error
    }
  }

  return (
    <>
      <Breadcrumb data={breadcrumbs} />
      <div className="tw-mb-6 tw-flex tw-items-center tw-justify-between">
        <h1 className="tw-font-display tw-font-semibold tw-text-4xl">{t("account.personal.title")}</h1>
      </div>
      <div className="tw-shadow tw-bg-white tw-rounded">
        <div className="tw-w-full tw-p-4 md:tw-p-12">
          {isEditing ? (
            <EditProfileForm
              initialValues={initialValues}
              currentEmail={user?.email ?? ""}
              isEmailVerified={user?.isEmailVerified ?? false}
              onCancel={() => setIsEditing(false)}
              onSubmit={handleSubmit}
            />
          ) : isChangingPassword ? (
            <ChangePasswordForm onCancel={() => setIsChangingPassword(false)} onSubmit={handleChangePassword} />
          ) : (
            <>
              <div className="tw-space-y-4">
                <div>
                  <label className="tw-text-sm tw-font-semibold tw-mb-1">{t("account.personal.form.name")}</label>
                  <p className="tw-text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                </div>
                <div>
                  <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-mb-1">
                    {t("account.personal.form.email")}
                    {user?.isEmailVerified ? (
                      <span
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-text-xs tw-font-medium
                          tw-text-success"
                        title={t("account.personal.emailVerified")}>
                        <HiCheckCircle className="tw-h-3.5 tw-w-3.5" />
                        {t("account.personal.emailVerified")}
                      </span>
                    ) : (
                      <span
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-text-xs tw-font-medium
                          tw-text-danger"
                        title={t("account.personal.emailUnverified")}>
                        <HiExclamationCircle className="tw-h-3.5 tw-w-3.5" />
                        {t("account.personal.emailUnverified")}
                      </span>
                    )}
                  </label>
                  <p className="tw-text-gray-900">{user?.email}</p>
                </div>
                <div>
                  <label className="tw-text-sm tw-font-semibold tw-mb-1">{t("account.personal.form.language")}</label>
                  <p className="tw-text-gray-900">
                    {SUPPORTED_LANGUAGES.find(lang => lang.value === user?.language)?.label}
                  </p>
                </div>
              </div>
              <div className="tw-flex tw-flex-wrap tw-gap-3 tw-mt-4 md:tw-mt-8">
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="tw-btn tw-btn-primary tw-whitespace-nowrap">
                  {t("account.personal.button.edit")}
                </button>
                <button
                  type="button"
                  onClick={() => setIsChangingPassword(true)}
                  className="tw-btn tw-btn-primary tw-whitespace-nowrap">
                  {t("account.personal.button.changePassword")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default PersonalSettingsContainer
